/**
 * A bot-vs-bot bout to WATCH (lane STUDIO-BOTDUEL: the studio tab บอทประลอง). The same bout as the balance sims
 * (systems/duelSim.ts `simBout`, scripts/balance/sim.ts `bout`), but stepped by the caller one 60 Hz tick at a time so a
 * renderer can draw it: `tick()` before every `World.step(1/60)`.
 *
 * Both seats are players of one World on the duel's v2 rules (`DuelMatchSystem.begin`, best of three, fought at the ring),
 * each played by the rival's mind (systems/duelBot.ts `thinkBot`) through the commands a person sends (`World.apply` with
 * the seat as sender). The primary player is fighter A (the camera's player), fighter B is added. Fair-arena numbers
 * (data/tournament.ts FAIR_ARENA) are held every tick, as the balance sim does, so only the art, the style and the level
 * differ. Deterministic: `seed` feeds the World's `Rng` and both minds' draws; time only through `World.step`.
 *
 * No rule is changed here: this only presses buttons for both seats. The stand-in's extra minds run for each seat too
 * (systems/duelBotSlide.ts slides, duelBotLock.ts lock forms and escapes): `extras` lends them the seat's view
 * (systems/botSeat.ts) and keeps each seat's own think timers apart.
 */
import { DUEL_COUNTERS, DUEL_MOVE, DUEL_STYLES, type DuelStyle } from '../data/duelMatch';
import { defaultAppearance, type Sex } from '../data/appearance';
import { moveLocked } from '../data/moveUnlocks';
import { getNeigong } from '../data/neigong';
import { MARTIAL_ARTS, RAGE, getArt, getSkill } from '../data/skills';
import { stacksOf } from '../status';
import { familyOf, isArmed } from '../data/weaponFamilies';
import { FAIR_ARENA } from '../data/tournament';
import { newProgress, type PlayerProgress } from '../progress';
import { mulberry32, type Rng } from '../rng';
import { World } from '../world';
import { BOT_ACT_FILTERS, BOT_SKILL_GATES, newBotMem, thinkBot, type BotAct, type BotMem, type BotSkill } from './duelBot';
import { duelMatchOf, type MatchState } from './duelMatch';
import { barSkillOk, skillBarOf } from './skillBar';
import { asBotSeat } from './botSeat';
import { duelBotLockOf } from './duelBotLock';
import { duelBotSlideOf, type BotSlideWhy } from './duelBotSlide';

/** What a seat's extra minds did this tick (for a log): a slide and why, a lock move pressed. */
export interface BotBoutAct {
  readonly side: 0 | 1;
  readonly t: number;
  readonly kind: 'slide' | 'lock';
  readonly why?: BotSlideWhy;
  readonly skillId?: string;
}

/** The fields of the extra minds that are one seat's own (their think clocks and memory), kept per seat. */
const SLIDE_KEYS = ['nextT', 'n'] as const;
const LOCK_KEYS = ['nextT', 'n', 'openedAt', 'judged'] as const;
type Mem = Record<string, unknown>;

export interface BotBoutSide {
  /** A martial art a player can learn (`BOT_BOUT_ARTS`). */
  readonly art: string;
  readonly sex: Sex;
  /** A `DUEL_STYLES` id: fierce / calm / sly. */
  readonly style: string;
  /** The mind's skill, 0-4 (data/duelMatch.ts DUEL_REACT / DUEL_SKILL). */
  readonly level: number;
  readonly name: string;
}

/** Arts a player can fight with: every art not hidden (the nine swords) and not the tournament's legendary prize. */
export const BOT_BOUT_ARTS: readonly string[] = Object.values(MARTIAL_ARTS)
  .filter((a) => !a.hidden && !a.legendary)
  .map((a) => a.id);

/** The neigong of each element (the art's element matched), as the balance sim gives it. */
const NEIGONG_OF: Record<string, string> = {
  yang: 'ember-heart',
  soft: 'cloud-heart',
  taiji: 'mist-breath',
  hard: 'bell-body',
  yin: 'mourning-heart',
};

/** A fighter's progress: the one art at the fair level, its element's neigong, the chosen sex, no gear look. */
export function botBoutProgress(side: BotBoutSide, seed: number): PlayerProgress {
  const pr = structuredClone({ ...newProgress(seed), location: 'plains' as const }) as PlayerProgress;
  const art = getArt(side.art);
  const ng = NEIGONG_OF[art.element] ?? 'cloud-heart';
  Object.assign(pr, {
    name: side.name,
    appearance: defaultAppearance(side.sex),
    arts: [side.art],
    activeArt: side.art,
    skillLevels: Object.fromEntries(art.skills.map((id) => [id, FAIR_ARENA.skillLevel])),
    neigong: { [ng]: Math.min(getNeigong(ng).maxLevel, FAIR_ARENA.neigongLevel) },
    activeNeigong: ng,
  });
  delete (pr as { skillBar?: unknown }).skillBar;
  delete (pr as { movesLearned?: unknown }).movesLearned;
  if (!isArmed(art)) {
    const eq = { ...pr.equipment };
    delete eq.weapon;
    pr.equipment = eq;
  }
  return pr;
}

const STEP = 1 / 60;
/** A bout not over after this many ticks (6 world minutes) is left as it stands. */
const CAP = 60 * 60 * 6;

interface Held {
  move: string;
  block: boolean;
  engage: boolean;
}

export class BotBout {
  readonly world: World;
  /** Entity ids of fighter A (the primary player) and B. */
  readonly ids: readonly [number, number];
  readonly sides: readonly [BotBoutSide, BotBoutSide];
  private readonly rng: Rng;
  private readonly mems: [BotMem, BotMem] = [newBotMem(), newBotMem()];
  private readonly held: [Held, Held] = [
    { move: '0,0', block: false, engage: false },
    { move: '0,0', block: false, engage: false },
  ];
  private ticks = 0;
  /** Each seat's own memory of the slide and lock minds. */
  private readonly extraMem: [{ slide: Mem; lock: Mem }, { slide: Mem; lock: Mem }] = [
    { slide: { nextT: 0, n: 0 }, lock: { nextT: 0, n: 0, openedAt: -1, judged: -1 } },
    { slide: { nextT: 0, n: 0 }, lock: { nextT: 0, n: 0, openedAt: -1, judged: -1 } },
  ];
  /** Slides and lock moves since the last `drainActs` (the log). */
  private acts: BotBoutAct[] = [];
  /** World seconds since the bout began. */
  t = 0;

  /** A private World on the plains with both fighters at the ring; the bout has begun (its countdown). `hour`: the light. */
  constructor(seed: number, a: BotBoutSide, b: BotBoutSide, hour = 10) {
    this.sides = [a, b];
    this.rng = mulberry32(seed ^ 0x51ed);
    // The game's own map (the World's default seed): `seed` only drives the draws, so every seed fights on the same plains.
    const w = new World({ rng: mulberry32(seed), progress: botBoutProgress(a, seed + 1), gm: false, story: false, hour });
    this.world = w;
    const ib = w.addPlayer(botBoutProgress(b, seed + 2), { x: w.player.x + 2, y: w.player.y });
    this.ids = [w.player.id, ib];
    for (const id of this.ids) {
      const e = w.get(id)!;
      e.maxHp = FAIR_ARENA.maxHp;
      e.hp = e.maxHp;
    }
    this.hold();
    const d = duelMatchOf(w);
    if (!d?.begin(this.ids[0], this.ids[1])) throw new Error('botBout: the bout could not begin');
  }

  /** The bout's state (null once it is gone). */
  get state(): Readonly<MatchState> | null {
    return duelMatchOf(this.world)?.current ?? null;
  }

  /** The bout is decided (its result shows) or ran past the cap. */
  get over(): boolean {
    const s = this.state;
    return !s || s.phase === 'result' || this.ticks >= CAP;
  }

  /** Both minds think and press; call once before every `World.step(1/60)`. */
  tick(): void {
    this.ticks++;
    this.t += STEP;
    this.hold();
    const d = duelMatchOf(this.world);
    const st = d?.current;
    if (!d || !st || st.phase !== 'fight') return;
    for (let i = 0; i < 2; i++) {
      const me = this.world.get(this.ids[i]!);
      const mv = d.fighterView(this.ids[i]!);
      const fv = d.fighterView(this.ids[1 - i]!);
      if (!me || !mv || !fv) continue;
      const act = thinkBot(
        {
          t: this.t,
          me: mv,
          foe: fv,
          skills: this.skillsOf(i),
          canDash: me.stamina >= 25,
          canEscape: me.stamina >= DUEL_MOVE.escape.cost,
          reach: familyOf(getArt(this.sides[i]!.art)).botReach,
          floor: familyOf(getArt(this.sides[i]!.art)).botFloor,
        },
        this.mems[i]!,
        this.styleOf(i),
        this.sides[i]!.level,
        this.rng,
      );
      if (act) this.send(i, BOT_ACT_FILTERS.reduce((a, f) => f(this.world, this.ids[i]!, a), act));
      this.extras(i, st);
    }
  }

  /** Slides and lock moves the seats made since the last call. */
  drainActs(): BotBoutAct[] {
    const a = this.acts;
    this.acts = [];
    return a;
  }

  /** The stand-in's slide and lock minds, run for seat `i` as if it were the stand-in (systems/botSeat.ts). */
  private extras(i: number, st: Readonly<MatchState>): void {
    const w = this.world;
    const view: MatchState = {
      ...st,
      pvp: false,
      foeId: this.ids[i]!,
      meId: this.ids[1 - i]!,
      style: this.styleOf(i),
      level: this.sides[i]!.level,
    };
    const mem = this.extraMem[i]!;
    const side = i === 0 ? 0 : 1;
    const run = (sys: object | null, keys: readonly string[], m: Mem, done: (latest: unknown) => void): void => {
      if (!sys) return;
      const o = sys as unknown as Mem & { latest: unknown; step(dt: number): void };
      const saved: Mem = {};
      for (const k of keys) saved[k] = o[k];
      Object.assign(o, m);
      const before = o.latest;
      try {
        asBotSeat(w, view, () => o.step(STEP));
      } finally {
        for (const k of keys) m[k] = o[k];
        Object.assign(o, saved);
      }
      if (o.latest !== before) done(o.latest);
    };
    run(duelBotSlideOf(w), SLIDE_KEYS, mem.slide, (l) => {
      const x = l as { why: BotSlideWhy } | null;
      if (x) this.acts.push({ side, t: this.t, kind: 'slide', why: x.why });
    });
    run(duelBotLockOf(w), LOCK_KEYS, mem.lock, (l) => {
      const x = l as { skillId: string } | null;
      if (x) this.acts.push({ side, t: this.t, kind: 'lock', skillId: x.skillId });
    });
  }

  private styleOf(i: number): DuelStyle {
    return DUEL_STYLES.find((x) => x.id === this.sides[i]!.style) ?? DUEL_STYLES[0]!;
  }

  /** The fair arena's base numbers, every tick (the World recalculates stats now and then). */
  private hold(): void {
    for (const id of this.ids) {
      const e = this.world.get(id);
      if (!e) continue;
      Object.assign(e, {
        atk: FAIR_ARENA.atk,
        magic: FAIR_ARENA.magic,
        def: FAIR_ARENA.def,
        magicDef: FAIR_ARENA.magicDef,
        crit: FAIR_ARENA.crit,
        magicCrit: FAIR_ARENA.magicCrit,
        hit: FAIR_ARENA.hit,
        magicHit: FAIR_ARENA.magicHit,
        dodge: FAIR_ARENA.dodge,
        toughness: FAIR_ARENA.toughness,
      });
    }
  }

  /** The seat's bar as the rival's mind reads it (as `DuelMatchSystem.botSkills`). */
  private skillsOf(i: number): BotSkill[] {
    const w = this.world;
    const id = this.ids[i]!;
    const me = w.get(id)!;
    const pr = w.players.get(id)!.progress;
    const out: BotSkill[] = [];
    skillBarOf(pr).forEach((sid, slot) => {
      if (!sid || !barSkillOk(pr, sid) || moveLocked(pr, sid)) return;
      const s = getSkill(sid);
      out.push({
        slot,
        id: sid,
        kind: s.kind,
        range: s.range || (s.aoe ?? 0),
        windup: s.windup,
        cooldown: s.cooldown,
        power: s.power,
        dash: !!s.dash,
        counter: s.kind === 'buff' && sid in DUEL_COUNTERS,
        ultimate: (s.rage ?? 0) >= RAGE.max,
        active: !!s.self && stacksOf(me.statuses, s.self.status) > 0,
        ready:
          (me.cooldowns[sid] ?? 0) <= 0 &&
          me.qi >= s.qi &&
          me.rage >= (s.rage ?? 0) &&
          BOT_SKILL_GATES.every((g) => g(w, id, sid)),
      });
    });
    return out;
  }

  /** What the mind chose, as that seat's commands (as `DuelMatchSystem.send`). */
  private send(i: number, a: BotAct): void {
    const w = this.world;
    const id = this.ids[i]!;
    const foeId = this.ids[1 - i]!;
    const me = w.get(id)!;
    const last = this.held[i]!;
    if (a.block !== last.block) w.apply({ t: 'block', on: a.block }, id);
    const mv = `${a.move[0].toFixed(2)},${a.move[1].toFixed(2)}`;
    if (mv !== last.move) w.apply({ t: 'moveDir', dx: a.move[0], dy: a.move[1] }, id);
    if (a.engage && !last.engage) w.apply({ t: 'engage', id: foeId }, id);
    if (!a.engage && last.engage && mv === '0,0') {
      const inp = w.players.get(id)?.input;
      if (inp) inp.engage = false;
      me.targetId = foeId;
    }
    if (a.skill !== null) {
      me.targetId = foeId;
      w.apply({ t: 'skill', slot: a.skill }, id);
    }
    if (a.jump) w.apply({ t: 'jump' }, id);
    if (a.attack) w.apply({ t: 'attack' }, id);
    if (a.dash) w.apply({ t: 'dash', dx: a.dash[0], dy: a.dash[1] }, id);
    this.held[i] = { move: mv, block: a.block, engage: a.engage };
  }
}
